import { visitStarted } from '../lib/completion-analytics';
import { cachedUpdates, INBOX_EVENT, refreshInbox, watchSeat } from '../lib/inbox';
import { seatKey } from '../lib/contribution-client';
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { isAppsInTossChannel } from '../lib/channel';
import { useLocalState } from '../state/LocalStateProvider';
import { NavigationIcon } from './NavigationIcon';

export function Layout() {
  const { state } = useLocalState();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [unread,setUnread] = useState(0);
  const appsInToss = isAppsInTossChannel();
  useEffect(()=>visitStarted(appsInToss),[appsInToss]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a route change must reset the old screen's scroll position.
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); }, [pathname]);
  useEffect(() => {
    if (!appsInToss) return;
    const onTossBack = () => {
      const historyIndex = Number(window.history.state?.idx);
      if (Number.isInteger(historyIndex) && historyIndex > 0) {
        navigate(-1);
      } else if (pathname !== '/') {
        // A deep link has no in-app history. Return to our start screen.
        navigate('/', { replace: true });
      } else {
        window.dispatchEvent(new Event('anjabom:toss-close'));
      }
    };
    window.addEventListener('anjabom:toss-back', onTossBack);
    return () => window.removeEventListener('anjabom:toss-back', onTossBack);
  }, [appsInToss, navigate, pathname]);
  useEffect(() => {
    let pointerActive = false;
    let frame = 0;
    const sync = () => {
      const editing = document.activeElement?.matches('input, textarea, select, [contenteditable="true"]') ?? false;
      // Keep the dock from appearing between a button's pointerdown and click.
      if (pointerActive && !editing) return;
      document.documentElement.dataset.editing = String(editing);
    };
    const pointerDown = () => { pointerActive = true; };
    const pointerEnd = () => { pointerActive = false; cancelAnimationFrame(frame); frame = requestAnimationFrame(sync); };
    document.addEventListener('focusin', sync); document.addEventListener('focusout', sync);
    document.addEventListener('pointerdown', pointerDown, true);
    document.addEventListener('pointerup', pointerEnd, true);
    document.addEventListener('pointercancel', pointerEnd, true);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('focusin', sync); document.removeEventListener('focusout', sync);
      document.removeEventListener('pointerdown', pointerDown, true);
      document.removeEventListener('pointerup', pointerEnd, true);
      document.removeEventListener('pointercancel', pointerEnd, true);
      delete document.documentElement.dataset.editing;
    };
  }, []);
  useEffect(()=>{const sync=()=>setUnread(cachedUpdates().length);window.addEventListener(INBOX_EVENT,sync);for(const seat of state.candidates)watchSeat(seatKey(seat));void refreshInbox([state.viewingPlan.eventTitle]).then(sync).catch(sync);return()=>window.removeEventListener(INBOX_EVENT,sync);},[state.candidates,state.viewingPlan.eventTitle]);
  return (
    <div className="shell">
      <a className="skip-link" href="#main">본문으로 바로 가기</a>
      {!appsInToss ? <header className="header">
        <div className="header-inner">
          <Link className="brand" to="/" aria-label="앉아봄 홈">
            <img src="/brand/symbol-seat-sightline.svg" alt="" />
            <span>앉아봄</span>
          </Link>
          <Link className="button" to="/inbox">소식{unread?` ${unread}`:''}</Link><nav className="header-actions" aria-label="주요 메뉴">
            <NavLink className="header-link" to="/coverage">확인 범위</NavLink>
            <NavLink className="header-link" to="/me/fit">내 조건</NavLink>
            <NavLink className="header-link" to="/visit-plan">관람 준비</NavLink>
            <NavLink className="header-link" to="/passport">내 기록</NavLink>
            {pathname !== '/' ? <NavLink className="header-link candidate-pill" to="/compare">후보 {state.candidates.length}</NavLink> : null}
          </nav>
        </div>
      </header> : null}
      <main className="main" id="main"><Outlet /></main>
      <footer className="footer">
        <div className="footer-inner">
          <span className="micro">앉아봄 · 아직 모르는 좌석은 모름이라고 해요.</span>
          <nav className="footer-links" aria-label="신뢰 메뉴">
            <Link to="/privacy">개인정보 처리방침</Link>
            <Link to="/terms">이용약관</Link>
            <Link to="/support">문의</Link>
            <Link to="/coverage">확인 범위</Link>
            <Link to="/settings">설정·데이터 관리</Link>
          </nav>
        </div>
      </footer>
        <nav className="mobile-dock" aria-label="빠른 메뉴">
          <NavLink to="/start"><NavigationIcon name="search" /><small>찾기</small></NavLink>
          <NavLink to="/compare"><NavigationIcon name="compare" /><small>후보{state.candidates.length > 0 ? ` ${state.candidates.length}` : ''}</small></NavLink>
          <NavLink to="/visit-plan"><NavigationIcon name="calendar" /><small>관람</small></NavLink>
          <NavLink to="/passport"><NavigationIcon name="journal" /><small>기록{unread?` · 새 소식 ${unread}`:''}</small></NavLink>
        </nav>
    </div>
  );
}
